/**
 * KinsCircle Phase 6D.2 — Legacy Chapter Card.
 *
 * STABILITY.md §13–§14:
 *   - Documentary-style "chapter" cards generated entirely from the
 *     existing semantic + historian engines. Pure read-only.
 *   - All prose is the user's own — chapter bodies are derived only from
 *     the indexed memories' captions / titles / tagged people / years.
 *   - NEVER fabricates AI prose, NEVER claims unverifiable facts.
 *   - Every chapter includes a mandatory "Based on N memor(y|ies) from
 *     YEAR–YEAR" citation.
 *   - Renders null when `legacyChapters` flag is OFF or no qualifying
 *     era / theme collections exist.
 */

import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  BookOpen,
  PlayCircle,
  Users,
  Calendar,
  Bookmark,
  BookmarkCheck,
} from 'lucide-react';
import {
  isLegacyChaptersEnabled,
  isStoryPlaybackEnabled,
  isLegacyBookmarksEnabled,
} from '@/lib/ai/featureFlag';
import {
  buildEraCollections,
  buildThemeCollection,
  buildPersonJourneyCollection,
  collectionToPlaybackSlides,
  rankCollectionMoments,
  type LegacyCollection,
} from '@/lib/ai/legacyCollectionEngine';
import {
  isBookmarked,
  saveLegacyBookmark,
  removeLegacyBookmark,
} from '@/lib/ai/legacyBookmarks';
import StoryPlaybackModal, { type PlaybackStory } from './StoryPlaybackModal';

const CHAPTER_TONES = [
  'from-amber-200 via-rose-200 to-fuchsia-300',
  'from-sky-200 via-violet-200 to-fuchsia-300',
  'from-emerald-200 via-teal-200 to-sky-200',
  'from-orange-200 via-pink-200 to-rose-300',
  'from-indigo-200 via-purple-200 to-fuchsia-300',
  'from-yellow-200 via-amber-200 to-orange-300',
];

const titleCase = (s: string): string =>
  s
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const formatYearRange = (r: { from: number | null; to: number | null }): string => {
  if (r.from && r.to) return r.from === r.to ? `${r.from}` : `${r.from}–${r.to}`;
  if (r.from) return `${r.from}`;
  if (r.to) return `${r.to}`;
  return '';
};

const LegacyChapterCard: React.FC = () => {
  if (!isLegacyChaptersEnabled()) return null;

  const chapters = useMemo<LegacyCollection[]>(() => {
    // Combine the strongest era / theme / person collections, then rank.
    const all = [
      ...buildEraCollections(6),
      ...buildThemeCollection(6),
      ...buildPersonJourneyCollection(4),
    ];
    if (!all.length) return [];
    return rankCollectionMoments(all)
      .map((r) => r.collection)
      .filter((c) => c.memoryCount >= 3)
      .slice(0, 6);
  }, []);

  const [active, setActive] = useState<LegacyCollection | null>(null);
  const [playOpen, setPlayOpen] = useState(false);
  const [bookmarkBump, setBookmarkBump] = useState(0);

  const story = useMemo<PlaybackStory | null>(() => {
    if (!active) return null;
    if (!isStoryPlaybackEnabled()) return null;
    const slides = collectionToPlaybackSlides(active, 14);
    if (!slides.length) return null;
    return {
      label: active.title,
      subtitle: active.citation,
      slides,
    };
  }, [active]);

  if (!chapters.length) return null;

  const toggleBookmark = (c: LegacyCollection) => {
    if (!isLegacyBookmarksEnabled()) return;
    const already = isBookmarked('chapter', c.id);
    if (already) removeLegacyBookmark('chapter', c.id);
    else saveLegacyBookmark({ kind: 'chapter', targetId: c.id, label: c.title });
    setBookmarkBump((n) => n + 1);
  };

  return (
    <section className="py-12 px-4 bg-gradient-to-b from-fuchsia-50/30 to-white">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="w-5 h-5 text-purple-600" />
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">
            Family Legacy Chapters
          </h2>
          <span className="text-[10px] uppercase tracking-wide font-semibold text-purple-700 bg-purple-100 px-2 py-0.5 rounded-full">
            Beta
          </span>
        </div>
        <p className="text-sm text-slate-600 max-w-2xl mb-6">
          Documentary-style chapters drawn from the moments your family has
          already saved — your own captions, your own people, your own years.
          Nothing is invented.
        </p>

        <div
          key={bookmarkBump}
          className="grid grid-cols-1 md:grid-cols-2 gap-5"
        >
          {chapters.map((c, idx) => {
            const tone = CHAPTER_TONES[idx % CHAPTER_TONES.length];
            const range = formatYearRange(c.yearRange);
            const saved = isBookmarked('chapter', c.id);
            return (
              <Card
                key={c.id}
                className="overflow-hidden border border-slate-100 shadow-md hover:shadow-lg transition-shadow"
              >
                <div className={`bg-gradient-to-br ${tone} p-6 md:p-7 min-h-[180px] relative`}>
                  <div className="flex items-start justify-between gap-3">
                    <Badge className="bg-white/80 text-slate-800 border border-white/60 hover:bg-white/80 text-[10px] uppercase tracking-wide">
                      Chapter
                    </Badge>
                    {isLegacyBookmarksEnabled() && (
                      <button
                        onClick={() => toggleBookmark(c)}
                        aria-label={saved ? 'Remove bookmark' : 'Save bookmark'}
                        className="p-1.5 rounded-full bg-white/70 hover:bg-white text-slate-700"
                      >
                        {saved ? (
                          <BookmarkCheck className="w-4 h-4" />
                        ) : (
                          <Bookmark className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl font-bold text-slate-900 leading-tight mt-4">
                    {c.title}
                  </h3>
                  {range && (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-sm text-slate-800/80">
                      <Calendar className="w-3.5 h-3.5" />
                      {range}
                    </div>
                  )}
                </div>

                <CardContent className="p-5 md:p-6 space-y-3.5">
                  {c.topPeople.length > 0 && (
                    <div className="flex items-start gap-2 text-sm text-slate-700">
                      <Users className="w-4 h-4 mt-0.5 shrink-0 text-slate-500" />
                      <div className="leading-relaxed">
                        <span className="font-semibold">Top family members:</span>{' '}
                        <span className="text-slate-600">
                          {c.topPeople.slice(0, 5).map(titleCase).join(', ')}
                          {c.topPeople.length > 5 &&
                            ` +${c.topPeople.length - 5} more`}
                        </span>
                      </div>
                    </div>
                  )}
                  {c.topThemes.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {c.topThemes.slice(0, 4).map((t) => (
                        <Badge
                          key={t}
                          variant="outline"
                          className="text-[11px] capitalize"
                        >
                          {t}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2">
                    <div className="text-[11px] italic text-slate-500">
                      {c.citation}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        setActive(c);
                        if (isStoryPlaybackEnabled()) setPlayOpen(true);
                      }}
                      disabled={!isStoryPlaybackEnabled()}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                      title={
                        isStoryPlaybackEnabled()
                          ? 'Open chapter'
                          : 'Story playback is disabled'
                      }
                    >
                      <PlayCircle className="w-4 h-4 mr-1.5" />
                      Open chapter
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <p className="text-[11px] text-slate-400 mt-6 text-center">
          Every chapter is anchored to memories you've already saved. No
          AI-generated prose. No invented relatives. No fabricated history.
        </p>
      </div>

      <StoryPlaybackModal
        open={playOpen}
        onOpenChange={setPlayOpen}
        story={story}
      />
    </section>
  );
};

export default LegacyChapterCard;
